import * as React from 'react';
import CalendarSurfaceStore from '../../../store/schema/CalendarSurfaceStore';
import convertLayoutPositionToStyles from '../../../utils/convertLayoutPositionToStyles';
import { CalendarSurfaceTransformProps } from '../../../interfaces/CalendarSurfaceTransformProps';
import { expand } from '../../expand';
import { getDateRange } from '../../getDateRange';
import { observer } from 'mobx-react';
import { SurfaceEventHandlers } from '../../../interfaces/SurfaceEventHandlers';

let styles = require('../../../SurfaceTemplates.scss');

/**
 * A distance from initial mouse down position we start tracking mouse move
 * Distance is calculated as a sqrt(pow(xi-xc,2) + pow(yi-yc,2))
 * */
const mouseTrackingDistance = 10;

export interface ColumnSelectionBehaviorProps {
    config: CalendarSurfaceTransformProps;
    surfaceStore: CalendarSurfaceStore;
    columnIndex: number;
    calendarFolderId?: string;
}

/**
 * Column behavior state
 * initialSelection - gris cell interval calculated on MouseDown
 * currentSelection - current grid cell calculted on MouseMove
 */
export interface ColumnSelectionBehaviorState {
    initialPoint: {
        pageX: number;
        pageY: number;
        currentTarget: HTMLElement;
        clientRect: ClientRect;
    };
    currentPoint: { pageY: number; clientRect: ClientRect };
}

/**
 * Columns behaviors
 * Starts tracking mouse on MouseDown event and selects grid cells as it moves.
 */
@observer
export default class ColumnSelectionBehavior extends React.Component<
    ColumnSelectionBehaviorProps,
    ColumnSelectionBehaviorState
> {
    constructor(props: ColumnSelectionBehaviorProps) {
        super(props);
        this.state = { initialPoint: null, currentPoint: null };
    }

    render() {
        return this.state.currentPoint && this.renderSelection();
    }

    /**
     * List of event handlers this behavior attaches to target
     */
    handlers(): SurfaceEventHandlers {
        return {
            onMouseDown: this.onMouseDown,
            onDoubleClick: this.onDoubleClick,
        };
    }

    private get selectedInterval() {
        const itemsLayout = this.props.config.itemsLayout;
        const columnIndex = this.props.columnIndex;

        let interval = itemsLayout.pointToInterval(
            columnIndex,
            this.state.initialPoint.pageY,
            this.state.initialPoint.clientRect
        );

        if (this.state.currentPoint) {
            let currentInterval = itemsLayout.pointToInterval(
                columnIndex,
                this.state.currentPoint.pageY,
                this.state.currentPoint.clientRect
            );

            expand(interval, currentInterval);
        }

        return interval;
    }

    private onMouseDown = (ev: React.MouseEvent<HTMLElement>) => {
        ev.preventDefault();
        if (ev.button == 0) {
            document.onmouseup = this.onMouseUp;
            document.onmousemove = this.onMouseMove;

            this.setState({
                initialPoint: {
                    pageX: ev.pageX,
                    pageY: ev.pageY,
                    currentTarget: ev.currentTarget,
                    clientRect: ev.currentTarget.getBoundingClientRect(),
                },
            });
        }
    };

    private onDoubleClick = (ev: React.MouseEvent<HTMLElement>) => {
        let { onSurfaceDoubleClick, itemsLayout } = this.props.config;
        if (onSurfaceDoubleClick) {
            let currentInterval = itemsLayout.pointToInterval(
                this.props.columnIndex,
                ev.pageY,
                ev.currentTarget.getBoundingClientRect()
            );
            const dateRange = getDateRange(
                currentInterval,
                this.props.surfaceStore,
                this.props.config.columnProvider
            );
            onSurfaceDoubleClick(
                dateRange.start,
                dateRange.end,
                false,
                false,
                this.props.calendarFolderId
            );
        }
    };

    private onMouseUp = (ev: MouseEvent) => {
        if (this.props.config.onSurfaceClick) {
            this.constructNewItem(ev);
        }
        this.setState({ initialPoint: null, currentPoint: null });
        document.onmouseup = null;
        document.onmousemove = null;
    };

    private constructNewItem(ev: MouseEvent) {
        const dateRange = getDateRange(
            this.selectedInterval,
            this.props.surfaceStore,
            this.props.config.columnProvider
        );
        this.props.config.onSurfaceClick(
            dateRange.start,
            dateRange.end,
            false,
            this.isMouseMoveTrackEnabled(ev),
            this.props.calendarFolderId
        );
    }

    private onMouseMove = (ev: MouseEvent) => {
        if (!this.state.initialPoint || !this.isMouseMoveTrackEnabled(ev)) {
            return;
        }

        this.setState({
            currentPoint: {
                pageY: ev.pageY,
                clientRect: this.state.initialPoint.currentTarget.getBoundingClientRect(),
            },
        });
    };

    private isMouseMoveTrackEnabled(ev: MouseEvent): boolean {
        let { initialPoint } = this.state;
        if (initialPoint) {
            return (
                Math.sqrt(
                    Math.pow(initialPoint.pageX - ev.pageX, 2) +
                        Math.pow(initialPoint.pageY - ev.pageY, 2)
                ) > mouseTrackingDistance
            );
        }
        return false;
    }

    private renderSelection(): JSX.Element {
        const interval = this.selectedInterval;
        const layout = this.props.config.itemsLayout.getVerticalLayout(interval);
        let style: React.CSSProperties = convertLayoutPositionToStyles(layout);
        return <div style={style} className={styles.selectionHighlight} />;
    }
}
